import {
  type QueryKey,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { bankingAccountsKeys } from "Api/Queries/Accounts/useBankingAccounts";
import { userInfoKeys } from "Auth/Api/Queries/useUserInfoQuery";
import type {
  EntityListOfBankAccountListItem,
  SetPrimaryBankAccountCommandResult,
} from "Infrastructure/Api/Api";
import { putBankingAccountsPublicIDPrimary } from "Infrastructure/Api/Api";
import { processResponseWithApiProblemDetails } from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Translations/Resources";
import { toast } from "sonner";

type Props = {
  onSuccess?: () => void;
};

export const usePrimaryBankingAccountMutation = ({ onSuccess }: Props = {}) => {
  const { t } = useResource();
  const queryClient = useQueryClient();

  return useMutation<
    SetPrimaryBankAccountCommandResult,
    // processResponseWithApiProblemDetails throws the raw FetchResponse
    // wrapper, not an Error instance.
    unknown,
    string,
    {
      previousLists: Array<
        [QueryKey, EntityListOfBankAccountListItem | undefined]
      >;
    }
  >({
    mutationFn: processResponseWithApiProblemDetails(
      (bankAccountPublicID: string) =>
        putBankingAccountsPublicIDPrimary(bankAccountPublicID),
    ),
    onMutate: async (bankAccountPublicID) => {
      await queryClient.cancelQueries({
        queryKey: bankingAccountsKeys.lists(),
      });

      const previousLists =
        queryClient.getQueriesData<EntityListOfBankAccountListItem>({
          queryKey: bankingAccountsKeys.lists(),
        });

      // Primary is per currency: the clicked account becomes primary and only
      // its currency siblings lose the flag — other currencies keep theirs.
      queryClient.setQueriesData<EntityListOfBankAccountListItem>(
        { queryKey: bankingAccountsKeys.lists() },
        (old) => {
          const target = old?.items.find(
            ({ publicID }) => publicID === bankAccountPublicID,
          );

          if (!old || !target) {
            return old;
          }

          return {
            ...old,
            items: old.items.map((item) =>
              item.currency === target.currency
                ? { ...item, isPrimary: item.publicID === bankAccountPublicID }
                : item,
            ),
          };
        },
      );

      return { previousLists };
    },
    onError: (_error, _variables, context) => {
      context?.previousLists.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
      toast.error(t(Resources.Hooks.BankingAccount.PrimaryAccountChangeError));
    },
    onSuccess: () => {
      onSuccess?.();
      toast.success(t(Resources.Hooks.BankingAccount.PrimaryAccountChanged));
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: bankingAccountsKeys.all,
      });
      queryClient.invalidateQueries({
        queryKey: userInfoKeys.all,
      });
    },
  });
};
